import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { VisionSample } from '../../core';
import type { DmsStatus } from '../../vision/dms';
import type { DmsLandmark, DmsMode } from '../hooks/useDms';

function metric(value: number | undefined, suffix = '', digits = 0): string {
  return value === undefined ? '—' : `${value.toFixed(digits)}${suffix}`;
}

export default function DmsEvidenceLens({
  visible,
  running,
  mode,
  status,
  sample,
  landmarks,
  sourceVideo,
}: {
  visible: boolean;
  running: boolean;
  mode: DmsMode;
  status: DmsStatus;
  sample: VisionSample | null;
  landmarks: DmsLandmark[] | null;
  sourceVideo: RefObject<HTMLVideoElement>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const latestLandmarks = useRef(landmarks);
  latestLandmarks.current = landmarks;

  useEffect(() => {
    if (!visible) return;
    const canvas = canvasRef.current;
    const video = sourceVideo.current;
    if (!canvas || !video) return;
    let frame = 0;
    let disposed = false;
    let lastDraw = 0;

    const draw = (at: number) => {
      if (disposed) return;
      const width = Math.max(1, Math.round(canvas.clientWidth * Math.min(window.devicePixelRatio || 1, 1.5)));
      const height = Math.max(1, Math.round(canvas.clientHeight * Math.min(window.devicePixelRatio || 1, 1.5)));
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      if (at - lastDraw >= 1000 / 30 || !running) {
        lastDraw = at;
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = '#070a10';
        ctx.fillRect(0, 0, width, height);
        if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
          const scale = Math.min(width / video.videoWidth, height / video.videoHeight);
          const drawWidth = video.videoWidth * scale;
          const drawHeight = video.videoHeight * scale;
          const x = (width - drawWidth) / 2;
          const y = (height - drawHeight) / 2;
          ctx.drawImage(video, x, y, drawWidth, drawHeight);
          const points = latestLandmarks.current;
          if (points) {
            ctx.fillStyle = 'rgba(94, 234, 212, .82)';
            for (let index = 0; index < points.length; index += 6) {
              const point = points[index];
              ctx.fillRect(x + point.x * drawWidth - 1, y + point.y * drawHeight - 1, 2, 2);
            }
            ctx.strokeStyle = 'rgba(255, 176, 92, .96)';
            ctx.lineWidth = Math.max(1.2, width / 420);
            for (const index of [33, 133, 159, 145, 362, 263, 386, 374]) {
              const point = points[index];
              if (!point) continue;
              ctx.beginPath();
              ctx.arc(x + point.x * drawWidth, y + point.y * drawHeight, 4, 0, Math.PI * 2);
              ctx.stroke();
            }
          }
        }
      }
      if (running) frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => { disposed = true; cancelAnimationFrame(frame); };
  }, [running, sourceVideo, visible]);

  const unavailable = status.kind === 'error';
  const source = mode === 'authorized-video'
    ? 'AUTHORIZED VIDEO · LOCAL INFERENCE'
    : mode === 'video'
      ? 'UPLOADED VIDEO · LOCAL INFERENCE'
      : mode === 'model'
        ? 'LIVE CAMERA · LOCAL INFERENCE'
        : 'REPLAY FALLBACK';
  const faceState = unavailable
    ? 'INFERENCE UNAVAILABLE'
    : sample?.present
      ? 'FACE TRACKED'
      : status.kind === 'loading'
        ? 'MODEL LOADING'
        : 'SEARCHING FACE';

  return (
    <aside
      className="dms-evidence-lens"
      data-status={status.kind}
      data-visible={visible}
      aria-hidden={!visible}
      aria-label="Driver Monitoring evidence preview"
    >
      <div className="dms-lens-heading">
        <div>
          <span>DMS / LOCAL EVIDENCE</span>
          <b>{faceState}</b>
        </div>
        <output>{sample?.present && !unavailable ? 'STREAM ACTIVE' : 'STREAM STANDBY'}</output>
      </div>
      <div className="dms-lens-body">
        <div className="dms-lens-data">
          <div className="dms-signal-flow" aria-label="Local driver monitoring data flow">
            <span>FRAME</span><i /><span>LANDMARKS</span><i /><span>METRICS</span>
          </div>
          <dl>
            <div><dt>EAR</dt><dd>{unavailable ? '—' : metric(sample?.ear, '', 2)}</dd></div>
            <div><dt>PERCLOS</dt><dd>{unavailable || !sample ? '—' : metric(sample.perclos * 100, '%')}</dd></div>
            <div><dt>BLINK RATE</dt><dd>{unavailable ? '—' : metric(sample?.blinkPm, ' / min')}</dd></div>
            <div><dt>EYES OFF ROAD</dt><dd>{unavailable ? '—' : metric(sample?.lookAwaySec, ' s', 1)}</dd></div>
            <div><dt>YAW / PITCH</dt><dd>{unavailable || !sample ? '—' : `${sample.yaw.toFixed(0)}° / ${sample.pitch.toFixed(0)}°`}</dd></div>
            <div><dt>FACE STATE</dt><dd>{unavailable || !sample ? '—' : sample.emotion.toUpperCase()}</dd></div>
            <div><dt>LANDMARKS</dt><dd>{unavailable || !landmarks ? '—' : landmarks.length}</dd></div>
            <div><dt>PROCESSING</dt><dd>{unavailable ? '—' : 'ON DEVICE'}</dd></div>
          </dl>
        </div>
        <div className="dms-lens-visual">
          <canvas ref={canvasRef} aria-label="Authorized vertical driver monitoring video with local face landmarks" />
          <div className="dms-lens-scan" aria-hidden="true" />
          <div className="dms-lens-axis" aria-hidden="true"><i /><i /><i /></div>
          <span className="dms-lens-corner top" aria-hidden="true" />
          <span className="dms-lens-corner bottom" aria-hidden="true" />
          <small>9:16 / LOCAL FRAME</small>
        </div>
      </div>
      <div className="dms-lens-source"><i aria-hidden="true" />{source}</div>
    </aside>
  );
}
